/**
 * Loads exactly the datasets the current layout needs — once each — and
 * hands them to widgets as a bag. Adding a widget that needs a new dataset
 * triggers one more query; removing the last widget that used one lets
 * the cache expire it.
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { isDatasetPermitted, requiredDatasets, widgetSourceIds, type Viewer } from './catalog';
import { DATASETS, type DatasetBag, type DatasetId } from './datasets';
import { allWidgets } from './layout';
import type { DashboardWindow } from './range';
import type { DashboardLayout } from './types';

export interface DashboardData {
  bag: DatasetBag;
  /** Datasets still on their first load. */
  pending: ReadonlySet<DatasetId>;
  errors: Partial<Record<DatasetId, string>>;
  isFetching: boolean;
  refetch: () => void;
}

/** Query options for one dataset — shared so ad-hoc readers hit the same cache entry as the board. */
export function datasetQueryOptions<K extends DatasetId>(id: K, window: DashboardWindow) {
  const spec = DATASETS[id];
  return {
    queryKey: ['intel-dash', id, spec.windowed ? window.fetchStart : null, spec.windowed ? window.currentEnd : null] as const,
    queryFn: () => spec.fetch({ window }),
    staleTime: spec.staleTimeMs ?? 60_000,
    retry: 1,
  };
}

export function useDashboardData(
  layout: DashboardLayout | null,
  window: DashboardWindow,
  viewer: Viewer,
  extra: readonly DatasetId[] = [],
): DashboardData {
  const idsKey = useMemo(() => {
    const ids = requiredDatasets(layout ? allWidgets(layout).flatMap(widgetSourceIds) : [], viewer);
    for (const id of extra) if (isDatasetPermitted(id, viewer)) ids.add(id);
    return Array.from(ids).sort().join(',');
  }, [layout, viewer, extra]);

  const ids = useMemo(() => (idsKey ? (idsKey.split(',') as DatasetId[]) : []), [idsKey]);

  const results = useQueries({
    queries: ids.map((id) => datasetQueryOptions(id, window)),
  });

  const stamp = results.map((r) => `${r.status}:${r.dataUpdatedAt}:${r.errorUpdatedAt}`).join('|');
  const isFetching = results.some((r) => r.isFetching);

  return useMemo(() => {
    const bag: DatasetBag = {};
    const pending = new Set<DatasetId>();
    const errors: Partial<Record<DatasetId, string>> = {};
    ids.forEach((id, i) => {
      const r = results[i];
      if (r.data !== undefined) {
        (bag as Record<string, unknown>)[id] = r.data;
      } else if (r.status === 'pending') {
        pending.add(id);
      }
      if (r.error) errors[id] = r.error instanceof Error ? r.error.message : String(r.error);
    });
    return {
      bag,
      pending,
      errors,
      isFetching,
      refetch: () => results.forEach((r) => void r.refetch()),
    };
    // `stamp` captures every status/data transition of `results`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, stamp, isFetching]);
}
